import { useEffect, type RefObject } from 'react';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Trap focus inside an open dialog and restore focus on close.
 */
export function useDialogFocus(
  active: boolean,
  dialogRef: RefObject<HTMLElement | null>,
  triggerRef?: RefObject<HTMLElement | null>
): void {
  useEffect(() => {
    if (!active || !dialogRef.current) return undefined;

    const previouslyFocused = document.activeElement;
    const root = dialogRef.current;
    const focusable = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));

    (focusable[0] ?? root).focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    const triggerElement = triggerRef?.current ?? null;

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const restoreTarget = triggerElement ?? previouslyFocused;
      if (restoreTarget instanceof HTMLElement) {
        restoreTarget.focus();
      }
    };
  }, [active, dialogRef, triggerRef]);
}
